import { nanoid } from 'nanoid';
import type { ReplayRequestInput } from '@terra/shared-dto-chat-types';
import { useProjectStore } from '../store/projectStore';
import { useReplaySessionStore } from '../store/replaySessionStore';
import { useReplayStore } from '../store/replay-store';
import { formatRequestToText } from './httpFormatter';
import {
  buildReplaySessionPayload,
  type ReplayOpenRequestInput,
} from './replayOpenRequest.utils';
import { sendReplayRequestWithRedirects } from './replayRedirect.utils';

export function looksLikeDisplayedBinaryMultipart(body?: string): boolean {
  if (!body) {
    return false;
  }

  if (body.includes('\0')) {
    return true;
  }

  const sample = body.slice(0, 4096);
  let nonPrintableCount = 0;

  for (const char of sample) {
    const code = char.charCodeAt(0);
    const isAllowed =
      code === 9 || code === 10 || code === 13 || (code >= 32 && code <= 126);

    if (!isAllowed) {
      nonPrintableCount++;
    }
  }

  return nonPrintableCount > sample.length * 0.05;
}

function mapPortalReplayInputToOpenRequest(
  input: ReplayRequestInput,
): ReplayOpenRequestInput {
  return {
    method: input.method ?? 'GET',
    url: input.url ?? '',
    headers: input.headers,
    body: input.body ?? '',
    httpVersion: input.protocol === 'http2' ? 'HTTP/2' : 'HTTP/1.1',
  };
}

export async function executePortalReplayRequest({
  input,
  applicationId,
  threadId,
}: {
  input: ReplayRequestInput;
  applicationId?: string;
  threadId?: string;
}) {
  const method = input.method ?? 'GET';
  const url = input.url ?? '';
  const requestId = nanoid();
  const replayRequest = {
    id: requestId,
    name: `${method} ${url}`,
    method,
    url,
    headers: input.headers,
    body: input.body ?? '',
    timestamp: new Date(),
  };

  const response = await sendReplayRequestWithRedirects({
    initialRequest: replayRequest,
    sendReplayRequest: useReplayStore.getState().sendReplayRequest,
    followRedirects: input.followRedirects ?? false,
    sendOptions: {
      protocol: input.protocol,
      source: 'copilot',
      threadId,
    },
  });

  const { activeCollectionId } = useReplaySessionStore.getState();
  const sessionPayload = buildReplaySessionPayload({
    requestData: mapPortalReplayInputToOpenRequest(input),
    applicationId: applicationId ?? useProjectStore.getState().targetId,
    collectionId: activeCollectionId,
    looksLikeDisplayedBinaryMultipart,
  });

  const requestData = formatRequestToText({
    method: replayRequest.method,
    url: replayRequest.url,
    headers: replayRequest.headers,
    body: replayRequest.body,
  });

  const sessionId = useReplaySessionStore.getState().addSession({
    ...sessionPayload,
    responseHistory: [
      {
        statusCode: response.statusCode,
        statusMessage: response.statusMessage,
        headers: response.headers,
        rawHeaders: response.rawHeaders,
        body: response.body,
        duration: response.duration,
        error: response.error,
        httpVersion: response.httpVersion,
        timestamp: new Date(),
        requestData,
      },
    ],
    currentResponseIndex: 0,
    protocol: input.protocol ?? sessionPayload.protocol,
  });

  return {
    sessionId,
    response,
  };
}
